import type { SyncPushResult, SyncPushProgress } from '@core/domain/sync/SyncPushResult.ts';

export type CombinedSyncPushSection<T, P = T> = {
  listPending: () => Promise<T[]>;
  beforePush?: (rows: T[]) => Promise<P[]>;
  markSynced: (ids: string[]) => Promise<void>;
  markFailed: (id: string, msg: string) => Promise<void>;
  afterAccepted?: (pending: T[], accepted: string[], pushResult: SyncPushResult) => Promise<string[] | void>;
  publicId: (row: T) => string;
};

type PushPort = {
  push: (
    body: Record<string, unknown>,
    pushOpts?: { onUploadProgress?: (ratio: number) => void },
  ) => Promise<SyncPushResult>;
};

async function pushSectionOneByOne<T, P>(
  sectionKey: 'entregas' | 'reposiciones',
  section: CombinedSyncPushSection<T, P>,
  pending: T[],
  pushPort: PushPort,
  reportProgress: (processed: number, uploadRatio?: number) => void,
  getProcessed: () => number,
  bumpProcessed: () => number,
): Promise<{ accepted: string[]; acceptedInferredFromTotales: boolean }> {
  const accepted: string[] = [];
  let acceptedInferredFromTotales = false;

  for (const row of pending) {
    const id = section.publicId(row);
    try {
      const prepared = section.beforePush ? await section.beforePush([row]) : [row as unknown as P];
      if (prepared.length === 0) {
        reportProgress(bumpProcessed());
        continue;
      }

      const pushResult = await pushPort.push(
        { [sectionKey]: prepared },
        { onUploadProgress: (ratio) => reportProgress(getProcessed(), ratio) },
      );
      if (pushResult.acceptedInferredFromTotales) acceptedInferredFromTotales = true;

      const acceptedRaw = pushResult.acceptedInferredFromTotales
        ? []
        : pushResult.acceptedPublicIds.filter((acceptedId) => acceptedId === id);
      let synced = acceptedRaw;
      if (section.afterAccepted) {
        const resolved = await section.afterAccepted([row], acceptedRaw, pushResult);
        if (resolved) synced = resolved;
      }
      if (synced.length > 0) {
        await section.markSynced(synced);
        accepted.push(...synced);
      }

      for (const rejected of pushResult.rejectedPublicIds ?? []) {
        if (rejected.publicId === id) await section.markFailed(id, rejected.error);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      await section.markFailed(id, message);
    }
    reportProgress(bumpProcessed());
  }

  return { accepted, acceptedInferredFromTotales };
}

export async function runCombinedSyncPushLoop<TA, PA, TB, PB>(opts: {
  pushPort: PushPort | null;
  entregas: CombinedSyncPushSection<TA, PA>;
  reposiciones: CombinedSyncPushSection<TB, PB>;
  flushPersist?: () => Promise<void>;
  onProgress?: (progress: SyncPushProgress) => void;
}): Promise<{
  entregasAccepted: string[];
  reposicionesAccepted: string[];
  acceptedInferredFromTotales: boolean;
}> {
  if (!opts.pushPort) {
    return { entregasAccepted: [], reposicionesAccepted: [], acceptedInferredFromTotales: false };
  }

  const pendingEntregas = await opts.entregas.listPending();
  const pendingReposiciones = await opts.reposiciones.listPending();
  if (pendingEntregas.length === 0 && pendingReposiciones.length === 0) {
    return { entregasAccepted: [], reposicionesAccepted: [], acceptedInferredFromTotales: false };
  }
  if (!globalThis.navigator?.onLine) {
    throw new Error('Sin conexión: no se pudieron enviar registros.');
  }

  const recordCount = pendingEntregas.length + pendingReposiciones.length;
  const progressTotal = recordCount + 1;
  let processed = 0;
  const reportProgress = (value: number, uploadRatio?: number) =>
    opts.onProgress?.({ processed: value, total: progressTotal, uploadRatio });
  const bumpProcessed = () => {
    processed += 1;
    return processed;
  };
  const getProcessed = () => processed;

  try {
    reportProgress(0);
    const entregaResult = await pushSectionOneByOne(
      'entregas',
      opts.entregas,
      pendingEntregas,
      opts.pushPort,
      reportProgress,
      getProcessed,
      bumpProcessed,
    );
    const reposicionResult = await pushSectionOneByOne(
      'reposiciones',
      opts.reposiciones,
      pendingReposiciones,
      opts.pushPort,
      reportProgress,
      getProcessed,
      bumpProcessed,
    );
    reportProgress(progressTotal);
    return {
      entregasAccepted: entregaResult.accepted,
      reposicionesAccepted: reposicionResult.accepted,
      acceptedInferredFromTotales:
        entregaResult.acceptedInferredFromTotales || reposicionResult.acceptedInferredFromTotales,
    };
  } finally {
    if (opts.flushPersist) await opts.flushPersist();
  }
}
